import './FeedbackModal.scss';
import { useForm, useFormWatch } from '@form-ts/react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useBoundAction } from 'src/hooks/useBoundAction';
import { Button } from 'src/modules/common/components/Button';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { FEEDBACK_SEND } from 'src/modules/feedback/actions/FeedbackActions';
import { FEEDBACK_FORM_DEFAULT_DATA } from 'src/modules/feedback/constants/formData';
import { FEEDBACK_FORM_SCHEMA } from 'src/modules/feedback/constants/schema';
import { FeedbackFormData } from 'src/modules/feedback/types/FeedbackFormData';
import { FeedbackForm } from 'src/modules/feedback/views/FeedbackForm';
import { FeedbackSent } from 'src/modules/feedback/views/FeedbackSent';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';

type Props = {
  readonly onClose: () => void;
};

export const FeedbackModal = ({ onClose }: Props): React.ReactElement => {
  const form = useForm<FeedbackFormData, FormError>('feedback.form', {
    reinitialize: false,
    initialValues: FEEDBACK_FORM_DEFAULT_DATA,
  });

  useFormValidator(form, FEEDBACK_FORM_SCHEMA);

  const submitting = useFormWatch(form, form.submitting.get);

  const submitForm = useBoundAction(FEEDBACK_SEND.request, form);
  const submitSucceeded = useFormWatch(form, form.submitSucceded.get);

  return (
    <>
      <ModalHeader
        label={<FormattedMessage id="feedback/modal/label"/>}
        title={<FormattedMessage id="feedback/modal/title"/>}
        handleClose={onClose}
        shaded={true}
      />

      <ModalBody shaded={true}>
        <div className="bp-feedback-modal__body">
          <div className="bp-feedback-modal__slogan">
            <img
              src="/images/FeedbackPicture.svg"
              alt=""
              width="238"
            />
          </div>

          <div className="bp-feedback-modal__form">
            {submitSucceeded
              ? <FeedbackSent/>
              : <FeedbackForm form={form}/>}
          </div>
        </div>
      </ModalBody>

      <ModalFooter narrowFooter={true}>
        <Button
          kind="secondary"
          onClick={onClose}
          autoFocus={true}
          fullWidth={true}
          disabled={submitting}
        >
          {submitSucceeded
            ? <FormattedMessage id="feedback/modal/sent/close"/>
            : <FormattedMessage id="feedback/modal/form/cancel"/>}
        </Button>

        {!submitSucceeded && (
          <Button
            disabled={submitting}
            kind="primary"
            loading={submitting}
            onClick={submitForm}
            fullWidth={true}
            icon={(
              <div className="bp-feedback-form__button-icon">
                <ArrowRightIcon/>
              </div>
            )}
          >
            <FormattedMessage id="feedback/modal/form/submit"/>
          </Button>
        )}
      </ModalFooter>
    </>
  );
};
